<div class="header">
  <div class="content">
    <div class="contentInner">
      <div class="pageTitle" appReveal>
        <div class="headline">Analytics</div>
      </div>
    </div>
    <app-enterprise-tab-navigator></app-enterprise-tab-navigator>
  </div>
</div>

<div class="content">
  <div class="contentInner">
    <h1 class="title">Dash Enterprise Analytics</h1>
    <p class="intro">Analytics provide valuable insights into how your meeting spaces are being used. Understand how
      your spaces are utilized, the methods used to book them, and discover opportunities to enhance
      efficiency in your workplace.</p>

    <h2 class="sectionHeader">Overview</h2>
    <p class="tip icon single-line">To collect analytics data, Dash version 1.5.18 or higher must be installed on your display devices.</p>
    <p>
      When analytics collection is enabled, Dash devices transmit meeting data to the web dashboard, where it can be
      accessed in the analytics section. Tracking can be enabled or disabled for each device group, and the data can be
      broken down by time and room.
    </p>
    <p>
      The analytics dashboard is organized into three main sections:
    </p>
    <ol>
      <li><b>Occupancy and Utilization: </b>This section provides charts showing how often your rooms are being used for
        meetings. It helps you assess whether your current room capacity is meeting your needs.</li>
      <li><b>Meeting Characteristics: </b>This section includes charts that provide insights into the meetings themselves,
        such as their duration and the methods used to book them.</li>
      <li><b>Efficiency: </b>This section presents charts that focus on Dash’s efficiency features, highlighting the
        time saved by actions like ending and extending meetings directly from the display, and automatically releasing
        rooms from abandoned meetings (if the <i>Check-in</i> feature is enabled).</li>
    </ol>
    <p>For more detailed information about the analytics dashboard and charts, please refer to the sections below.  For
      more information about the data collected, please see the <i>Data</i> section at the end of this document.</p>

    <h2 class="sectionHeader" id="compatibility">Compatibility</h2>
    <p>Dash Enterprise analytics is fully compatible with the following calendar connections:</p>
    <ul>
      <li>Microsoft 365 Enterprise Application</li>
      <li>Microsoft 365 business/personal account</li>
      <li>Google Workspace Service account</li>
      <li>Google Workspace standard/personal account</li>
    </ul>
    <p>Dash Enterprise analytics is partially compatible with the <i>Device calendar</i> connection type, however you
      may encounter the following issues (depending on your particular setup):</p>
    <ul>
      <li>Recording the method used to book meetings is not supported, so meetings will always be recorded as booked from your calendar, even if they're booked from a Dash display.</li>
      <li>Dash does not record how long meetings are booked in advance - meetings will be recorded as having been booked at the time they start.</li>
      <li>If the same room is shown on two or more different devices, the room may be duplicated in the analytics dashboard (and the events recorded from each device will appear separately).  We recommend using a single device per room.</li>
      <li>It is possible that different rooms (displayed on two different devices) may be combined into a single room in the dashboard.</li>
    </ul>
    <p>If you have any questions about the compatibility of Dash Enterprise analytics or would like help setting it up, please <a routerLink="/contact">contact us</a> for assistance.</p>

    <h2 class="sectionHeader" id="eventCollection">Enabling event collection</h2>
    <app-illustration alt="The controls to enable analytics collection in Dash Enterprise" src="/assets/images/dash-enterprise-enable-analytics.png" webpSrc="/assets/images/dash-enterprise-enable-analytics.webp" width="1262" height="659" class="illustration"></app-illustration>
    <p>
      You can enable analytics event collection for each device group you wish to track.  To enable it, go to the
      <i>Configuration</i> tab of a device group, toggle on <i>Collect analytics events from devices in this group</i>,
      and save the configuration. Once enabled, the devices in that group will begin transmitting analytics events.
    </p>
    <p class="tip icon">If you disable event collection for a group, the devices in that group will stop recording and
      transmitting events to the web dashboard.
    </p>

    <h2 class="sectionHeader" id="dashboard">The analytics dashboard</h2>
    <p>The analytics dashboard consists of a room and date selector at the top, followed by three reports below.</p>

    <h4>Selecting rooms</h4>
    <app-illustration alt="Selecting the rooms to show analytics for in Dash Enterprise" src="/assets/images/dash-enterprise-analytics-room-chooser.png" webpSrc="/assets/images/dash-enterprise-analytics-room-chooser.webp" width="533" height="511" class="illustration small"></app-illustration>
    <p>The room selector displays all rooms with available analytics data, grouped by device group (based on the device
      from which the data was transmitted). You can filter this list by both room names and device groups.</p>
    <p>To view analytics for specific rooms, simply select one or more rooms from the list.</p>
    <p class="tip icon">
      In Dash Enterprise, a device group is a collection of devices, and each device can be associated with multiple rooms (or a single room can be linked to multiple devices).
      <br><br>
      If a room is associated with multiple device groups, it will appear once under each associated group.
    </p>

    <h4>Selecting a date range</h4>
    <app-illustration alt="Selecting a date range for analytics in Dash Enterprise" src="/assets/images/dash-enterprise-analytics-date-chooser.png" webpSrc="/assets/images/dash-enterprise-analytics-date-chooser.webp" width="795" height="593" class="illustration small"></app-illustration>
    <p>The date selector allows you to choose a date range to select the timeframe of the analytics dashboard.  You can
      choose to view analytics collected between two dates, since a particular date, or in the last x days, weeks, or
      months (where x is a value you select).  The oldest date or timeframe that you can select is based on the earliest
      analytics data collected, across all rooms.</p>
    <p>The <i>Last</i> and <i>Since</i> options automatically update based on the current date. If you select one of these
      options and return at a later time, the displayed dates will automatically adjust.</p>
    <p class="tip icon">
      The time of analytics events is recorded according to the local timezone of each meeting room. For example, if you have meeting
      rooms in two different countries, and each room hosts a meeting at 10am local time, both events will appear as
      starting at 10am in the analytics dashboard.
    </p>

    <h2 class="sectionHeader" id="reports">Interactive reports</h2>
    <p>The analytics dashboard shows three reports for the selected rooms and time range, arranged into three tabs of
      interactive charts.  Some ways to interact with the charts include:</p>
    <ul>
      <li>Hover over a point on the chart to show the chart's value at that point.  Some charts will also show the value in alternative measurements.</li>
      <li>For charts with multiple series, you can toggle individual series on or off by clicking the series name in the legend (located below or to the right of the chart).</li>
      <li>To export a chart, simply click the export button <span class="export-icon"></span> in the top-right corner, where you'll have options to save the chart as PNG, SVG, or CSV.</li>
    </ul>

    <h3>Occupancy and Utilization</h3>
    <h4>Number of meetings</h4>
    <app-illustration alt="The 'Number of meetings' Dash Enterprise analytics chart" src="/assets/images/dash-enterprise-analytics-number-of-meetings.png" webpSrc="/assets/images/dash-enterprise-analytics-number-of-meetings.webp" width="795" height="535" class="illustration small"></app-illustration>
    <p>This chart displays the total number of meetings held in the selected rooms over the specified time period.
      The total number of meetings and an average number of meetings per room per day are shown above the main chart.
      The average per room per day is calculated based on days with at least one meeting, so weekends and
      days without meetings are excluded. The main chart can be toggled between three views:</p>
    <ul>
      <li><b>Area: </b>A stacked area chart. If fewer than five rooms are selected, the chart shows each room separately.
        The rooms are stacked, with the total height on any given day representing the combined number of meetings across
        all selected rooms.</li>
      <li><b>Line: </b>A line chart where each room is represented by an individual line, showing the number of meetings
        in each room on a particular day, without stacking.</li>
      <li><b>Pie: </b>A pie chart showing the proportion of total meetings held in each room.</li>
    </ul>

    <h4>Amount of time rooms in use</h4>
    <app-illustration alt="The 'Number of meeting hours' Dash Enterprise analytics chart" src="/assets/images/dash-enterprise-analytics-number-of-meeting-hours.png" webpSrc="/assets/images/dash-enterprise-analytics-number-of-meeting-hours.webp" width="795" height="535" class="illustration small"></app-illustration>
    <p>This chart displays the total amount of time the selected rooms were in use during the specified time period.
      Unlike the <i>Number of Meetings chart</i>, this chart is weighted by time, meaning a day with a few long meetings will
      appear larger than a day with many short meetings. Above the main chart, you’ll find the total amount of time and
      the average time per room per day. The average per room per day is calculated based on days with at least one meeting,
      so weekends and days without meetings are excluded. The main chart can be toggled between three views:</p>
    <ul>
      <li><b>Area: </b>A stacked area chart. If fewer than five rooms are selected, the chart displays each room separately.
        The rooms are stacked, with the total height on any given day representing the combined time that all selected
        rooms were occupied that day.</li>
      <li><b>Line: </b>A line chart where each room is represented by an individual line, showing the amount of time the
        room was in use on a specific day, without stacking.</li>
      <li><b>Pie: </b>A pie chart showing the proportion of total occupied time for each room.</li>
    </ul>

    <h4>Utilization</h4>
    <app-illustration alt="The 'Room Utilization' Dash Enterprise analytics chart" src="/assets/images/dash-enterprise-analytics-utilization.png" webpSrc="/assets/images/dash-enterprise-analytics-utilization.webp" width="948" height="536" class="illustration small"></app-illustration>
    <p>This chart displays a grid with a cell for each hour of the day and day of the week, shaded to represent the
      percentage of time the selected rooms were occupied during that specific hour and day combination across the
      selected time period.
    </p>
    <p>For example, if you select two meeting rooms and a time period that includes three Tuesdays, and the room
      schedules contain the following meetings:</p>
    <ul>
      <li><b>Room 1: </b>Tuesday 1: 10am-11am, Tuesday 2: 10am-10:20am, Tuesday 3: 10:30am-11am</li>
      <li><b>Room 2: </b>Tuesday 1: 10:30am-11am, Tuesday 3: 10am-11am</li>
    </ul>
    <p>Then the chart would show a utilization of 56% (3h 20m) for the Tuesday, 10am-11am cell.  Hover over any cell to
      see the exact utilization percentage and the total time spent in use.</p>

    <h4>Room occupancy</h4>
    <app-illustration alt="The 'Room Occupancy' Dash Enterprise analytics chart" src="/assets/images/dash-enterprise-analytics-occupancy.png" webpSrc="/assets/images/dash-enterprise-analytics-occupancy.webp" width="948" height="536" class="illustration small"></app-illustration>
    <p>This chart shows average number of participants in each room, as a percentage of the room's capacity.  Participants
      who have declined are not included in the count, but participants who have accepted, are tentative, or have not responded are included.  Note that
      this chart is only available if you have connected Dash to your room calendars using a connection method that
      supports room capacities (i.e. <i>Microsoft 365 enterprise application</i> or <i>Google Workspace service account</i>).</p>
    <p>The occupancy percentage is calculated based on the total time the selected rooms were in use during the selected
      time period. For example, if a room has a capacity of 8 people, and there’s a one-hour meeting with 4 participants
      followed by a two-hour meeting with 7 participants, the average occupancy for that room will be 75%.</p>

    <p class="tip icon">
      Keep in mind that Dash only tracks participants listed in the meeting invite. Therefore, the actual occupancy
      percentage may vary if some participants are present in the room but not recorded in the invite, or if some
      invited participants join remotely.
    </p>


    <h3>Meeting Characteristics</h3>
    <h4>Meeting lengths</h4>
    <app-illustration alt="The 'Meeting lengths' Dash Enterprise analytics chart" src="/assets/images/dash-enterprise-analytics-meeting-lengths.png" webpSrc="/assets/images/dash-enterprise-analytics-meeting-lengths.webp" width="795" height="535" class="illustration small"></app-illustration>
    <p>This chart displays the distribution of meetings based on their duration (at the time the meeting ended). The
      chart categorizes meetings into the following time ranges:</p>
    <ul>
      <li>15 minutes or less</li>
      <li>Between 15 and 30 minutes</li>
      <li>Between 30 minutes and 1 hour</li>
      <li>Between 1 hour and 1 hour 30 minutes</li>
      <li>Between 1 hour 30 minutes and 2 hours</li>
      <li>More than 2 hours</li>
    </ul>

    <p>Each column in the chart is further broken down by the method used to book the meeting, allowing you to identify
      how different types of meetings are typically scheduled based on their length.</p>

    <h4>Booking method</h4>
    <app-illustration alt="The 'Booking method' Dash Enterprise analytics chart" src="/assets/images/dash-enterprise-analytics-booking-method.png" webpSrc="/assets/images/dash-enterprise-analytics-booking-method.webp" width="795" height="535" class="illustration small"></app-illustration>
    <p>This chart provides a breakdown of the total time spent in the selected rooms, categorized by the method used to
      book the meetings. The chart focuses on the amount of time used, rather than the number of meetings, meaning
      that longer meetings will have a greater influence on the results.  The booking methods are:
    </p>
    <ul>
      <li><b>Calendar booking: </b>Booked outside of Dash (e.g. through Outlook or Google Calendar)</li>
      <li><b>Instant booking from the display: </b>Booked from the dash display using the <i>Book now</i> feature</li>
      <li><b>Instant booking from find rooms: </b>Booked from another dash display using the <i>Find free rooms</i> feature</li>
      <li><b>Later booking from the display: </b>Booked from the Dash display using the <i>Book a later time</i> feature</li>
    </ul>

    <h4>How far meetings are booked in advance</h4>
    <app-illustration alt="The 'How far meetings are booked in advance' Dash Enterprise analytics chart" src="/assets/images/dash-enterprise-analytics-meeting-booking-distribution.png" webpSrc="/assets/images/dash-enterprise-analytics-meeting-booking-distribution.webp" width="795" height="535" class="illustration small"></app-illustration>
    <p>
      This chart provides a breakdown of how far in advance meetings were booked, relative to their scheduled start time.
      The time ranges are as follows:
    </p>
    <ul>
      <li>15 minutes or less before</li>
      <li>Between 15 minutes and 1 hour before</li>
      <li>Between 1 hour and 1 day before</li>
      <li>Between 1 day and 1 week before</li>
      <li>Between 1 week and 30 days before</li>
      <li>More than 30 days before</li>
    </ul>
    <p>
      The chart is also broken down by the method used to book the room, allowing you to see how far in advance meetings
      are typically scheduled for each booking method.
    </p>

    <h4>Rooms booked by find rooms</h4>
    <app-illustration alt="The 'Rooms booked by find rooms' Dash Enterprise analytics chart" src="/assets/images/dash-enterprise-analytics-find-rooms.png" webpSrc="/assets/images/dash-enterprise-analytics-find-rooms.webp" width="795" height="535" class="illustration small"></app-illustration>
    <p>
      This chart shows the total amount of time booked in the selected rooms using the <i>Find free rooms</i> feature during
      the selected time period. The rooms are listed in descending order, with the room that has the most booked time appearing first.
    </p>
    <p>
      The main chart can be toggled between a bar chart and pie chart view.
    </p>

    <h3>Efficiency</h3>
    <h4>Reclaimed time</h4>
    <app-illustration alt="The 'Reclaimed time' Dash Enterprise analytics chart" src="/assets/images/dash-enterprise-analytics-reclaimed-time.png" webpSrc="/assets/images/dash-enterprise-analytics-reclaimed-time.webp" width="795" height="535" class="illustration small"></app-illustration>
    <p>This chart shows the amount of time reclaimed by ending meetings before their originally scheduled
      end time (based on the end time as it was set when the meeting started).  The reclaimed time is
      broken down into the following categories:</p>

    <ul>
      <li><b>Check-in:</b> If the <i>Check-in</i> feature is enabled, this shows the amount of time reclaimed when
        meetings are not checked in and are automatically ended by the Dash display</li>
      <li><b>Ending from the display:</b> The amount of time reclaimed when staff manually press the <i>End now</i> button on the
        Dash display</li>
      <li><b>Ending elsewhere:</b> The amount of time reclaimed by ending a meeting early from Outlook or Google Calendar.
        Note that the time is only counted if the meeting was ended while it was in progress.  If a meeting is ended
        before it starts, it is not counted toward reclaimed time.</li>
    </ul>

    <h4>Reclaimed time by day of week</h4>
    <app-illustration alt="The 'Reclaimed time by day' Dash Enterprise analytics chart" src="/assets/images/dash-enterprise-analytics-reclaimed-day.png" webpSrc="/assets/images/dash-enterprise-analytics-reclaimed-day.webp" width="795" height="535" class="illustration small"></app-illustration>
    <p>This chart shows the amount of time that has been reclaimed by ending meetings before their originally scheduled
      end time (based on the end time as it was set when the meeting started).  The reclaimed time is
      grouped by the day of the week on the meetings occurred, and is broken down into the following categories:</p>

    <ul>
      <li><b>Check-in:</b> If the <i>Check-in</i> feature is enabled, this represents the time reclaimed when meetings
        are not checked in and are automatically ended by the Dash display.</li>
      <li><b>Ending from the display:</b> The time reclaimed when staff manually press the <i>End now</i> button on the
        Dash display</li>
      <li><b>Ending elsewhere:</b> The time reclaimed when meetings are ended early through Outlook or Google Calendar.
        Note that time is only counted if the meeting was ended while it was in progress. If a meeting is ended
        before it starts, it is not counted toward reclaimed time.</li>
    </ul>

    <h4>Time utilized by extending meetings</h4>
    <app-illustration alt="The 'Time utilized by extending' Dash Enterprise analytics chart" src="/assets/images/dash-enterprise-analytics-extend.png" webpSrc="/assets/images/dash-enterprise-analytics-extend.webp" width="795" height="535" class="illustration small"></app-illustration>
    <p></p>
    <p>This chart shows the amount of time utilized by extending meetings from the Dash display (using the <i>Extend</i>
      function). The total amount of time and an average amount of time utilized per room day are shown above the main chart.
      The average is calculated based on days with at least one meeting, excluding weekends and days without meetings.
      The main chart can be toggled between three views:</p>
    <ul>
      <li><b>Area: </b>A stacked area chart.  If fewer than five rooms are selected, the chart shows each room separately.
        The rooms are stacked, and the total height for any given day represents the combined time utilized by extending
        meetings across all rooms.</li>
      <li><b>Line: </b>Similar to the area chart, but each room is represented by a separate line, showing the time
        utilized by extensions on a specific day (without stacking).</li>
      <li><b>Pie: </b>A pie chart that illustrates the proportion of total time utilized by extensions in each room.</li>
    </ul>

    <h4>Number of times meeting extended</h4>
    <app-illustration alt="The 'Number of times meeting extended' Dash Enterprise analytics chart" src="/assets/images/dash-enterprise-analytics-number-of-times-extended.png" webpSrc="/assets/images/dash-enterprise-analytics-number-of-times-extended.webp" width="795" height="535" class="illustration small"></app-illustration>
    <p>For meetings that were extended from the Dash display, this chart shows how many times an individual meeting was
      extended.  The data is grouped based on the number of extensions:</p>
    <ul>
      <li>Extended once</li>
      <li>Extended twice</li>
      <li>Extended three or more times</li>
    </ul>

    <h4>How meetings were ended</h4>
    <app-illustration alt="The 'How meetings were ended' Dash Enterprise analytics chart" src="/assets/images/dash-enterprise-analytics-how-meetings-ended.png" webpSrc="/assets/images/dash-enterprise-analytics-how-meetings-ended.webp" width="795" height="535" class="illustration small"></app-illustration>
    <p>This chart shows the proportion of meetings that were ended in the following ways:</p>
    <ul>
      <li><b>Normally: </b>The meeting ended at its originally scheduled time (defined as the end time of the meeting as it was when the meeting started)</li>
      <li><b>Ended early: </b>The meeting ended earlier than its originally scheduled time, ether because the <i>End now</i> button was pressed on the Dash display, or the meeting was updated in the calendar while it was in progress</li>
      <li><b>Extended: </b>The meeting ended later than originally scheduled, either due to the <i>Extend</i> button being pressed on the Dash display or the meeting being updated in the calendar while it was in progress.</li>
      <li><b>Not checked in: </b>The meeting was automatically ended by Dash because the <i>Check-in</i> button was not pressed.</li>
    </ul>

    <h4>Check-in rate</h4>
    <app-illustration alt="The 'Check-in rate' Dash Enterprise analytics chart" src="/assets/images/dash-enterprise-analytics-check-in-rate.png" webpSrc="/assets/images/dash-enterprise-analytics-check-in-rate.webp" width="795" height="535" class="illustration small"></app-illustration>
    <p>If the <i>Check-in</i> feature is enabled on your Dash displays, this chart will show the percentage of meetings that have been checked in.</p>
    <p>If the <i>Check-in</i> feature is not enabled, only instant bookings made directly from the display (which are automatically checked in) will be counted as checked in.</p>

    <h4>&nbsp;</h4>

    <h2 class="sectionHeader" id="data">Data</h2>
    <p>If you enable analytics for a device group, devices in the group will collect
      the following information, which will be stored in our secure cloud environment:</p>

    <h4>For each meeting room calendar</h4>
    <ul>
      <li>The name of the meeting room</li>
      <li>The id of the meeting room (either the email address of the room, or a random id, depending on the calendar service you use)</li>
      <li>The capacity of the room (if present)</li>
    </ul>

    <h4>For each meeting/event</h4>
    <ul>
    <li>The start and end time of the event</li>
    <li>The number of attendees (no details about the names or email addresses of the attendees)</li>
    <li>Whether or not the meeting has been checked in</li>
    <li>The timing of any events that occur while the meeting is in progress (which includes extending the meeting, ending it early, and when your Dash display removes an abandoned meeting from the room calendar)</li>
    </ul>

    <p>Analytics does not capture any personal information such as the name and notes of meetings, or any details about attendees (apart from how many attendees are attending).</p>

    <p class="tip icon"><span>Analytics is disabled on your Dash display devices by default.  You can enable analytics on any
      device group, and the devices in that group will collect the information described above.  If you do not wish to
      use analytics, it is possible to disable analytics for your entire organization - please
      <a routerLink="/contact">contact us</a> for more information.</span></p>

    <h4>&nbsp;</h4>

  </div>
</div>
